/**
 * Rich text (pasted HTML, or plain text) → LaTeX.
 *
 * Headings, paragraphs, bold/italic/underline/strike/code, sub- and superscripts, links,
 * ordered and unordered lists (including the "fake" lists Word pastes as styled
 * paragraphs), description lists, tables, block quotes and code blocks become standard
 * LaTeX; every special character is escaped. Colours, fonts, sizes, margins and images
 * have no standard-LaTeX equivalent and are dropped (images leave a comment).
 *
 * The HTML is parsed by the site's own tokenizer into a tree and walked; nothing is ever
 * handed to the browser as markup.
 */

import {
  type ElementNode,
  type HtmlNode,
  parseHtmlDocument,
} from "@/lib/tool-kit/developer/html/tree";
import {
  attr,
  BLOCKS,
  type Ctx,
  collectInline,
  DEFAULT_RICH_TEXT_OPTIONS,
  DROPPED,
  decodeText,
  escapeRich,
  flatten,
  imageComments,
  PLAIN,
  type RichTextOptions,
  type RichTextStats,
  renderSegs,
  type Seg,
  styleMap,
} from "@/lib/tool-kit/latex-dev/rich-text-inline";
import { renderTable } from "@/lib/tool-kit/latex-dev/rich-text-tables";

export interface RichTextResult {
  /** The converted text: the body, or a whole document when `fullDocument` is on. */
  latex: string;
  body: string;
  /** `\usepackage` lines the body needs, in preamble order. */
  packages: string[];
  stats: RichTextStats;
  /** Characters pdfLaTeX's default setup may reject (Greek, CJK, arrows, emoji…). */
  nonLatin: string[];
}

const RE_LOOKS_LIKE_HTML = /<\/?[a-z][a-z0-9:-]*(?:\s[^>]*)?>/i;
const RE_PARAGRAPH_SPLIT = /\r?\n\s*\r?\n/;
const RE_BULLET_LINE = /^\s*[-*\u2022]\s+(.*)$/;
const RE_NUMBER_LINE = /^\s*\d{1,3}[.)]\s+(.*)$/;
const RE_NEWLINE = /\r?\n/;
const RE_MSO_LIST = /mso-list\s*:\s*(?!none|ignore)/i;
const RE_MSO_LEVEL = /level(\d+)/i;
const RE_MSO_ORDERED = /^\s*(?:\(?[0-9a-zA-Z]{1,4}[.)])/;
const RE_HEADING = /^h[1-6]$/;
const RE_FIRST_NEWLINE = /^\n/;
const RE_TRAILING_SPACE = /\s+$/;
const RE_ITEM_INDENT = /^ {2}/;
const RE_MSO_IGNORE = /mso-list\s*:\s*ignore/i;
const RE_TRAILING_BREAKS = /(?:\s*\\\\\s*)+$/;
const RE_NBSP = /\u00a0/g;
const RE_BLANK = /^[\s\u00a0\u200b]*$/;
const RE_END_VERBATIM = /\\end\{verbatim\}/;
const RE_LIST_STYLE = /list-style(?:-type)?\s*:\s*([a-z-]+)/i;

const SECTIONING: Record<"section" | "chapter", string[]> = {
  section: [
    "section",
    "subsection",
    "subsubsection",
    "paragraph",
    "subparagraph",
    "subparagraph",
  ],
  chapter: [
    "chapter",
    "section",
    "subsection",
    "subsubsection",
    "paragraph",
    "subparagraph",
  ],
};

const ENUM_STYLES: Record<string, string> = {
  decimal: "arabic",
  "lower-alpha": "alph",
  "lower-latin": "alph",
  "upper-alpha": "Alph",
  "upper-latin": "Alph",
  "lower-roman": "roman",
  "upper-roman": "Roman",
};
const TYPE_ATTR: Record<string, string> = {
  "1": "arabic",
  a: "alph",
  A: "Alph",
  i: "roman",
  I: "Roman",
};
const COUNTERS = ["enumi", "enumii", "enumiii", "enumiv"];
/** The counter style and punctuation LaTeX's own labels use at each depth. */
const DEFAULT_ENUM = ["arabic", "alph", "roman", "Alph"];

export function looksLikeHtml(text: string): boolean {
  return RE_LOOKS_LIKE_HTML.test(text);
}

export function emptyStats(): RichTextStats {
  return {
    headings: 0,
    paragraphs: 0,
    listItems: 0,
    tables: 0,
    links: 0,
    linksDropped: 0,
    codeBlocks: 0,
    quotes: 0,
    images: 0,
    rowspans: 0,
    deepLists: 0,
  };
}

function makeCtx(opts: RichTextOptions): Ctx {
  return {
    opts,
    stats: emptyStats(),
    packages: new Set(),
    nonLatin: new Set(),
    prev: "",
    inHeading: false,
    inLink: false,
    inCell: false,
    inItem: 0,
    enumDepth: 0,
    listDepth: 0,
  };
}

function synth(name: string, children: HtmlNode[]): ElementNode {
  return {
    kind: "element",
    name,
    sourceName: name,
    attrs: [],
    selfClosing: false,
    children,
    hasEndTag: true,
    start: 0,
    end: 0,
    innerStart: 0,
    innerEnd: 0,
    line: 0,
    foreign: false,
  };
}

function indent(text: string, pad = "  "): string {
  return text
    .split("\n")
    .map((line) => (line === "" ? line : pad + line))
    .join("\n");
}

function textAlignOf(el: ElementNode): "center" | "right" | undefined {
  const value = (
    styleMap(el).get("text-align") ??
    attr(el, "align") ??
    ""
  ).toLowerCase();
  if (value === "center") {
    return "center";
  }
  return value === "right" ? "right" : undefined;
}

function withAlignment(
  blocks: string[],
  align: "center" | "right" | undefined,
  ctx: Ctx
) {
  if (align === undefined || blocks.length === 0 || ctx.inCell) {
    return blocks;
  }
  const env = align === "center" ? "center" : "flushright";
  return [`\\begin{${env}}\n${blocks.join("\n\n")}\n\\end{${env}}`];
}

/* ---------------------------------------------------------------- Word lists */

interface MsoItem {
  level: number;
  ordered: boolean;
  nodes: HtmlNode[];
}

/** The paragraph itself when `node` is one of Word's styled-paragraph list items. */
function msoParagraph(node: HtmlNode): ElementNode | null {
  if (
    node.kind === "element" &&
    node.name === "p" &&
    RE_MSO_LIST.test(attr(node, "style") ?? "")
  ) {
    return node;
  }
  return null;
}

function isIgnoredMarker(el: ElementNode): boolean {
  return RE_MSO_IGNORE.test(attr(el, "style") ?? "");
}

function plainText(nodes: HtmlNode[]): string {
  let out = "";
  for (const node of nodes) {
    if (node.kind === "text") {
      out += decodeText(node.raw);
    } else if (node.kind === "element" && !DROPPED.has(node.name)) {
      out += plainText(node.children);
    }
  }
  return out;
}

/** The paragraph's children without the bullet or number Word put in an "Ignore" span. */
function stripMarker(nodes: HtmlNode[]): { nodes: HtmlNode[]; marker: string } {
  let marker = "";
  const kept: HtmlNode[] = [];
  for (const node of nodes) {
    if (node.kind === "element" && isIgnoredMarker(node)) {
      marker += plainText(node.children);
    } else if (node.kind === "element") {
      const inner = stripMarker(node.children);
      marker += inner.marker;
      kept.push({ ...node, children: inner.nodes });
    } else {
      kept.push(node);
    }
  }
  return { nodes: kept, marker };
}

function msoItem(el: ElementNode): MsoItem {
  const style = attr(el, "style") ?? "";
  const level = Number.parseInt(RE_MSO_LEVEL.exec(style)?.[1] ?? "1", 10);
  const { nodes, marker } = stripMarker(el.children);
  return {
    level,
    ordered: RE_MSO_ORDERED.test(marker.replace(RE_NBSP, " ")),
    nodes,
  };
}

/** Nest consecutive Word list paragraphs into ul/ol/li elements by their level. */
function buildMsoLists(items: MsoItem[]): ElementNode[] {
  const tops: ElementNode[] = [];
  const stack: { level: number; list: ElementNode }[] = [];
  for (const item of items) {
    const li = synth("li", item.nodes);
    while (stack.length > 0 && (stack.at(-1)?.level ?? 0) > item.level) {
      stack.pop();
    }
    const top = stack.at(-1);
    if (top !== undefined && top.level === item.level) {
      top.list.children.push(li);
      continue;
    }
    const list = synth(item.ordered ? "ol" : "ul", [li]);
    const parentLi = top?.list.children.at(-1);
    if (parentLi?.kind === "element") {
      parentLi.children.push(list);
    } else {
      tops.push(list);
    }
    stack.push({ level: item.level, list });
  }
  return tops;
}

/** Index after a run of Word list paragraphs starting at `from`, and its lists. */
function takeMsoRun(
  nodes: HtmlNode[],
  from: number
): { end: number; lists: ElementNode[] } {
  const items: MsoItem[] = [];
  let end = from;
  for (let i = from; i < nodes.length; i += 1) {
    const node = nodes[i];
    const paragraph = msoParagraph(node);
    if (paragraph) {
      items.push(msoItem(paragraph));
      end = i + 1;
    } else if (!(node.kind === "text" && node.raw.trim() === "")) {
      break;
    }
  }
  return { end, lists: buildMsoLists(items) };
}

/* -------------------------------------------------------------------- Blocks */

function inlineOf(nodes: HtmlNode[], ctx: Ctx): Seg[] {
  const segs: Seg[] = [];
  collectInline(nodes, segs, PLAIN, ctx);
  return segs;
}

function isBlank(segs: Seg[]): boolean {
  return segs.every(
    (seg) =>
      seg.kind === "break" ||
      seg.kind === "image" ||
      (seg.kind === "text" && RE_BLANK.test(seg.text))
  );
}

function paragraphText(segs: Seg[], ctx: Ctx): string {
  if (isBlank(segs)) {
    return "";
  }
  const text = renderSegs(segs, ctx).trim();
  return text.replace(RE_TRAILING_BREAKS, "").trim();
}

function renderHeading(el: ElementNode, ctx: Ctx): string[] {
  const level = Number.parseInt(el.name.slice(1), 10);
  ctx.inHeading = true;
  const segs = inlineOf(el.children, ctx);
  const text = flatten(renderSegs(segs, ctx, " "));
  ctx.inHeading = false;
  if (text === "") {
    return [];
  }
  ctx.stats.headings += 1;
  const command = SECTIONING[ctx.opts.headingScheme][level - 1];
  const star = ctx.opts.numberedHeadings ? "" : "*";
  return [`\\${command}${star}{${text}}`];
}

function enumLabel(el: ElementNode, depth: number): string | undefined {
  const typeAttr = attr(el, "type");
  const css = RE_LIST_STYLE.exec(attr(el, "style") ?? "")?.[1];
  const firstLi = el.children.find(
    (c): c is ElementNode => c.kind === "element" && c.name === "li"
  );
  const liCss = firstLi
    ? RE_LIST_STYLE.exec(attr(firstLi, "style") ?? "")?.[1]
    : undefined;
  const style =
    (typeAttr ? TYPE_ATTR[typeAttr] : undefined) ??
    ENUM_STYLES[(css ?? liCss ?? "").toLowerCase()];
  if (style === undefined || style === DEFAULT_ENUM[depth - 1]) {
    return undefined;
  }
  const counter = ["enumi", "enumii", "enumiii", "enumiv"][depth - 1];
  const label = ["labelenumi", "labelenumii", "labelenumiii", "labelenumiv"][
    depth - 1
  ];
  return `\\renewcommand{\\${label}}{\\${style}{${counter}}.}`;
}

function renderItem(li: ElementNode, ctx: Ctx): string {
  ctx.inItem += 1;
  const blocks = blocksOf(li.children, ctx);
  ctx.inItem -= 1;
  if (blocks.length === 0) {
    return "";
  }
  const [first, ...rest] = blocks;
  const startsWithList = first.startsWith("\\begin{");
  let text = startsWithList ? `\\item\n${first}` : `\\item ${first}`;
  for (const block of rest) {
    text += block.startsWith("\\begin{") ? `\n${block}` : `\n\n${block}`;
  }
  return indent(text).replace(RE_ITEM_INDENT, "");
}

function flatList(el: ElementNode, ctx: Ctx): string {
  const ordered = el.name === "ol";
  const parts: string[] = [];
  let n = 0;
  for (const child of el.children) {
    if (child.kind === "element" && child.name === "li") {
      n += 1;
      const text = flatten(blocksOf(child.children, ctx).join(" "));
      parts.push(`${ordered ? `${n}.` : "\\textbullet"}~${text}`);
      ctx.stats.listItems += 1;
    }
  }
  return parts.join(" ");
}

/** Label and start-number lines an ordered list needs after \begin{enumerate}. */
function listSetup(el: ElementNode, ordered: boolean, depth: number): string[] {
  if (!ordered || depth > 4) {
    return [];
  }
  const lines: string[] = [];
  const label = enumLabel(el, depth);
  if (label) {
    lines.push(indent(label));
  }
  const start = Number.parseInt(attr(el, "start") ?? "", 10);
  if (Number.isFinite(start) && start > 1) {
    lines.push(indent(`\\setcounter{${COUNTERS[depth - 1]}}{${start - 1}}`));
  }
  return lines;
}

function renderList(el: ElementNode, ctx: Ctx): string[] {
  if (ctx.inCell) {
    const flat = flatList(el, ctx);
    return flat === "" ? [] : [flat];
  }
  const ordered = el.name === "ol";
  const env = ordered ? "enumerate" : "itemize";
  ctx.listDepth += 1;
  if (ordered) {
    ctx.enumDepth += 1;
  }
  if (ctx.listDepth > 4) {
    ctx.stats.deepLists += 1;
  }
  const items: string[] = [];
  for (const child of el.children) {
    if (child.kind === "element" && child.name === "li") {
      const item = renderItem(child, ctx);
      if (item !== "") {
        ctx.stats.listItems += 1;
        items.push(item);
      }
    }
  }
  const depth = ctx.enumDepth;
  ctx.listDepth -= 1;
  if (ordered) {
    ctx.enumDepth -= 1;
  }
  if (items.length === 0) {
    return [];
  }
  const head = [`\\begin{${env}}`, ...listSetup(el, ordered, depth)];
  return [
    [...head, ...items.map((item) => indent(item)), `\\end{${env}}`].join("\n"),
  ];
}

function renderDescription(el: ElementNode, ctx: Ctx): string[] {
  const lines: string[] = [];
  let term: string | null = null;
  for (const child of el.children) {
    if (child.kind !== "element") {
      continue;
    }
    const text = flatten(blocksOf(child.children, ctx).join(" "));
    if (child.name === "dt") {
      term = text;
    } else if (child.name === "dd") {
      lines.push(`\\item[${term ?? ""}] ${text}`);
      term = null;
    }
  }
  if (lines.length === 0) {
    return [];
  }
  ctx.stats.listItems += lines.length;
  return [
    [
      "\\begin{description}",
      ...lines.map((line) => indent(line)),
      "\\end{description}",
    ].join("\n"),
  ];
}

function renderPre(el: ElementNode, ctx: Ctx): string[] {
  const text = plainText(el.children)
    .replace(RE_NBSP, " ")
    .replace(RE_FIRST_NEWLINE, "")
    .replace(RE_TRAILING_SPACE, "");
  if (text.trim() === "") {
    return [];
  }
  ctx.stats.codeBlocks += 1;
  if (!RE_END_VERBATIM.test(text)) {
    return [`\\begin{verbatim}\n${text}\n\\end{verbatim}`];
  }
  const lines = text
    .split(RE_NEWLINE)
    .map((line) => `\\texttt{${escapeRich(line, ctx)}}`);
  return [`\\begin{quote}\n${lines.join(" \\\\\n")}\n\\end{quote}`];
}

function renderQuote(el: ElementNode, ctx: Ctx): string[] {
  const blocks = blocksOf(el.children, ctx);
  if (blocks.length === 0) {
    return [];
  }
  ctx.stats.quotes += 1;
  return [`\\begin{quote}\n${blocks.join("\n\n")}\n\\end{quote}`];
}

function cellContent(children: HtmlNode[], ctx: Ctx): string {
  const saved = ctx.inCell;
  ctx.inCell = true;
  const out = blocksOf(children, ctx).join(" ");
  ctx.inCell = saved;
  return out;
}

function renderParagraphEl(el: ElementNode, ctx: Ctx): string[] {
  const blocks = blocksOf(el.children, ctx);
  return withAlignment(blocks, textAlignOf(el), ctx);
}

function blockElement(el: ElementNode, ctx: Ctx): string[] {
  const { name } = el;
  if (RE_HEADING.test(name)) {
    return ctx.inCell ? blocksOf(el.children, ctx) : renderHeading(el, ctx);
  }
  switch (name) {
    case "ul":
    case "ol":
      return renderList(el, ctx);
    case "dl":
      return renderDescription(el, ctx);
    case "table":
      return [renderTable(el, ctx, cellContent)].filter((t) => t !== "");
    case "blockquote":
      return renderQuote(el, ctx);
    case "pre":
      return renderPre(el, ctx);
    case "hr":
      return ["\\par\\noindent\\rule{\\linewidth}{0.4pt}\\par"];
    case "center":
      return withAlignment(blocksOf(el.children, ctx), "center", ctx);
    default:
      return renderParagraphEl(el, ctx);
  }
}

/** Convert a run of sibling nodes into blocks (paragraphs, lists, tables…). */
function blocksOf(nodes: HtmlNode[], ctx: Ctx): string[] {
  const out: string[] = [];
  let pending: Seg[] = [];
  const flush = () => {
    const text = paragraphText(pending, ctx);
    if (text !== "") {
      if (ctx.inItem === 0 && !ctx.inCell) {
        ctx.stats.paragraphs += 1;
      }
      out.push(text);
    }
    out.push(...imageComments(pending));
    pending = [];
  };
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (msoParagraph(node)) {
      flush();
      const run = takeMsoRun(nodes, i);
      for (const list of run.lists) {
        out.push(...renderList(list, ctx));
      }
      i = run.end - 1;
    } else if (
      node.kind === "element" &&
      BLOCKS.has(node.name) &&
      !DROPPED.has(node.name)
    ) {
      flush();
      out.push(...blockElement(node, ctx));
    } else {
      collectInline([node], pending, PLAIN, ctx);
    }
  }
  flush();
  return out;
}

/* ---------------------------------------------------------------- Plain text */

function plainListBlock(lines: string[], ctx: Ctx): string | null {
  const bullets = lines.map((line) => RE_BULLET_LINE.exec(line));
  const numbers = lines.map((line) => RE_NUMBER_LINE.exec(line));
  const bulleted = bullets.every((m) => m !== null);
  const numbered = numbers.every((m) => m !== null);
  if (lines.length < 2 || !(bulleted || numbered)) {
    return null;
  }
  const env = bulleted ? "itemize" : "enumerate";
  const matches = bulleted ? bullets : numbers;
  ctx.stats.listItems += lines.length;
  const items = matches.map((m) => {
    const text = escapeRich(flatten(m?.[1] ?? ""), ctx);
    return indent(`\\item ${text}`);
  });
  return [`\\begin{${env}}`, ...items, `\\end{${env}}`].join("\n");
}

function plainBlocks(text: string, ctx: Ctx): string[] {
  const blocks: string[] = [];
  for (const chunk of text.split(RE_PARAGRAPH_SPLIT)) {
    const lines = chunk.split(RE_NEWLINE).filter((line) => line.trim() !== "");
    if (lines.length === 0) {
      continue;
    }
    const list = plainListBlock(lines, ctx);
    if (list !== null) {
      blocks.push(list);
      continue;
    }
    const segs: Seg[] = [];
    collectInline([{ kind: "text", raw: lines.join(" ") }], segs, PLAIN, ctx);
    const body = paragraphText(segs, ctx);
    if (body !== "") {
      ctx.stats.paragraphs += 1;
      blocks.push(body);
    }
  }
  return blocks;
}

/* ------------------------------------------------------------------ Public API */

const PACKAGE_ORDER = ["booktabs", "ulem", "hyperref"];

function packageLine(name: string): string {
  return name === "ulem"
    ? "\\usepackage[normalem]{ulem}"
    : `\\usepackage{${name}}`;
}

function documentClass(opts: RichTextOptions): string {
  return opts.headingScheme === "chapter" ? "report" : "article";
}

/**
 * Convert pasted rich text. `input` is HTML when it contains tags, otherwise plain text
 * (blank lines separate paragraphs; lines starting `- ` or `1. ` become lists).
 */
export function richTextToLatex(
  input: string,
  options: Partial<RichTextOptions> = {}
): RichTextResult {
  const opts = { ...DEFAULT_RICH_TEXT_OPTIONS, ...options };
  const ctx = makeCtx(opts);
  const blocks = looksLikeHtml(input)
    ? blocksOf(parseHtmlDocument(input).root.children, ctx)
    : plainBlocks(input, ctx);
  const body = blocks.join("\n\n");
  const packages = PACKAGE_ORDER.filter((name) => ctx.packages.has(name)).map(
    packageLine
  );
  const latex = opts.fullDocument
    ? [
        `\\documentclass{${documentClass(opts)}}`,
        ...packages,
        "",
        "\\begin{document}",
        "",
        body,
        "",
        "\\end{document}",
      ].join("\n")
    : body;
  return {
    latex,
    body,
    packages,
    stats: ctx.stats,
    nonLatin: [...ctx.nonLatin],
  };
}
